import React, { useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { appPath } from '../utils/appRouting';
import { Button } from '../components/ui/button';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ToggleGroup, ToggleGroupItem } from '../components/ui/toggle-group';
import { 
  Mail, 
  Lock, 
  User, 
  ChevronRight,
  CheckCircle,
  AlertCircle,
  Eye,
  EyeOff,
  Sparkles,
  ArrowLeft,
  Loader2
} from 'lucide-react';
import { auth } from '../firebase';
import { signInWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { getBackendBaseUrl } from '../utils/backendBaseUrl';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';

const API_URL = getBackendBaseUrl();

const Register = () => {
  const navigate = useNavigate();

  const registerSchema = useMemo(
    () =>
      z
        .object({
          nome: z.string().min(1, 'Inserisci il tuo nome').transform((v) => v.trim()),
          cognome: z.string().min(1, 'Inserisci il tuo cognome').transform((v) => v.trim()),
          teachingRole: z.enum(['sostegno', 'curricolare'], {
            required_error: 'Seleziona il tuo ruolo docente',
          }),
          isClassCoordinator: z.boolean().optional(),
          email: z
            .string()
            .min(1, 'Inserisci la tua email')
            .email('Inserisci un\'email valida')
            .transform((v) => v.trim().toLowerCase()),
          password: z.string()
            .min(8, 'La password deve essere di almeno 8 caratteri')
            .regex(/[A-Z]/, 'La password deve contenere almeno una lettera maiuscola')
            .regex(/[0-9]/, 'La password deve contenere almeno un numero'),
          confirmPassword: z.string().min(1, 'Conferma la password'),
        })
        .refine((v) => v.password === v.confirmPassword, {
          path: ['confirmPassword'],
          message: 'Le password non coincidono',
        }),
    []
  );

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(registerSchema),
    defaultValues: {
      nome: '',
      cognome: '',
      teachingRole: 'sostegno',
      isClassCoordinator: false,
      email: '',
      password: '',
      confirmPassword: '',
    },
  });

  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  const passwordValue = watch('password');
  const passwordStrength = useMemo(() => {
    const v = passwordValue || '';
    let score = 0;
    if (v.length >= 8) score++;
    if (/[A-Z]/.test(v)) score++;
    if (/[0-9]/.test(v)) score++;
    if (/[^A-Za-z0-9]/.test(v)) score++;
    return score;
  }, [passwordValue]);

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [newsletterOptIn, setNewsletterOptIn] = useState(false);

  const teachingRole = watch('teachingRole');
  const isClassCoordinator = watch('isClassCoordinator');

  // Nota: la parte pagamenti/piani è stata rimossa; rimane solo registrazione FREE.

  const onSubmit = async (values) => {
    setError('');

    if (!termsAccepted) {
      setError('Devi accettare i Termini e Condizioni per continuare');
      return;
    }

    setLoading(true);

    try {
      // Step 1: Registra l'utente nel backend
      const response = await fetch(`${API_URL}/api/payments/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          nome: values.nome,
          cognome: values.cognome,
          email: values.email,
          password: values.password,
          terms_accepted: termsAccepted,
          newsletter_opt_in: newsletterOptIn,
          teachingRole: values.teachingRole,
          isClassCoordinator: Boolean(values.isClassCoordinator)
        })
      });

      const data = await response.json();

      if (!response.ok) {
        // Gli errori di validazione Pydantic (422) restituiscono detail come array
        const detail = data.detail;
        const message = Array.isArray(detail)
          ? detail.map(e => e.msg || String(e)).join(', ')
          : (typeof detail === 'string' ? detail : 'Errore durante la registrazione');
        throw new Error(message);
      }

      // Step 2: Login automatico con Firebase per inviare email di verifica
      try {
        const userCredential = await signInWithEmailAndPassword(
          auth, 
          values.email, 
          values.password
        );
        
        // Step 3: Invia email di verifica tramite Firebase
        await sendEmailVerification(userCredential.user);
        
        // Logout dopo aver inviato l'email (l'utente deve verificare prima)
        await auth.signOut();
        
      } catch (firebaseError) {
        console.error('Firebase email verification error:', firebaseError);
        // Continua comunque, l'utente può richiedere l'email dal login
      }

      setRegisteredEmail(values.email);
      setSuccess(true);

    } catch (err) {
      setError(err.message || 'Errore durante la registrazione');
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="h-[100dvh] overflow-y-auto bg-gradient-to-b from-muted/30 to-background text-foreground flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-card rounded-2xl shadow-xl border border-border p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-foreground mb-4">
            Registrazione completata!
          </h2>
          <p className="text-muted-foreground mb-6">
            Ti abbiamo inviato un'email di verifica a <strong>{registeredEmail}</strong>. 
            Clicca sul link nella email per attivare il tuo account.
          </p>

          <div className="space-y-3">
            <Button
              onClick={() => navigate(appPath('/login'))}
              className="w-full ds-btn-gradient"
            >
              Vai al Login
              <ChevronRight className="w-4 h-4 ml-2" />
            </Button>
            <p className="text-sm text-muted-foreground">
              Non hai ricevuto l'email? Controlla la cartella spam.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[100dvh] overflow-y-auto bg-gradient-to-b from-muted/30 to-background text-foreground scroll-smooth">
      {/* Header */}
      <header className="bg-background/80 backdrop-blur-md border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link to={appPath('/')} className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-brand to-brand-2 rounded-xl flex items-center justify-center shadow-elev1">
                <span className="text-yellow-400 font-bold text-lg">S+</span>
              </div>
              <div>
                <h1 className="text-lg font-bold text-foreground">SOSTEGNO PRO+</h1>
              </div>
            </Link>
            <Link
              to={appPath('/login')}
              className="text-muted-foreground hover:text-foreground font-medium flex items-center justify-end"
            >
              <span className="hidden sm:inline">Hai già un account?&nbsp;</span>
              <span className="text-brand">Accedi</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Registration Form */}
      <div className="flex items-center justify-center py-8 px-4">
        <div className="max-w-xl w-full">
          {/* Back to Pricing */}
          <Link 
            to="/pricing" 
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Torna ai piani
          </Link>

          <div className="bg-card rounded-2xl shadow-xl border border-border p-6 md:p-8">
            {/* Header */}
            <div className="text-center mb-6">
              <div className="w-14 h-14 bg-gradient-to-br from-brand to-brand-2 rounded-xl flex items-center justify-center mx-auto mb-4 shadow-elev1">
                <span className="text-yellow-400 font-bold text-xl">S+</span>
              </div>
              <h2 className="text-2xl font-bold text-foreground mb-2">
                Crea il tuo account
              </h2>
            </div>

            <div className="mb-6">
              <p className="text-sm text-muted-foreground text-center">
                La registrazione è disponibile in modalità <strong>Gratuita</strong>.
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="flex items-center gap-3 bg-destructive/10 border border-destructive/20 text-destructive px-4 py-3 rounded-lg mb-6">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {/* Form */}
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
              {/* Nome e Cognome */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="nome">Nome</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="nome"
                      type="text"
                      placeholder="Mario"
                      className="pl-10"
                      {...register('nome')}
                      aria-invalid={Boolean(errors.nome)}
                    />
                  </div>
                  {errors.nome?.message ? (
                    <p className="text-xs text-destructive">{String(errors.nome.message)}</p>
                  ) : null}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cognome">Cognome</Label>
                  <Input
                    id="cognome"
                    type="text"
                    placeholder="Rossi"
                    {...register('cognome')}
                    aria-invalid={Boolean(errors.cognome)}
                  />
                  {errors.cognome?.message ? (
                    <p className="text-xs text-destructive">{String(errors.cognome.message)}</p>
                  ) : null}
                </div>
              </div>

              {/* Ruolo docente + Coordinatore */}
              <div className="space-y-2">
                <Label>Ruolo docente</Label>
                <div className="rounded-xl border border-border bg-background p-3">
                  <input type="hidden" {...register('teachingRole')} />
                  <input type="hidden" {...register('isClassCoordinator')} />

                  <ToggleGroup
                    type="single"
                    value={teachingRole}
                    onValueChange={(v) => {
                      if (!v) return;
                      setValue('teachingRole', v, { shouldValidate: true, shouldDirty: true });
                    }}
                    className="justify-start"
                  >
                    <ToggleGroupItem value="sostegno" aria-label="Docente di sostegno" className="flex-1">
                      Sostegno
                    </ToggleGroupItem>
                    <ToggleGroupItem value="curricolare" aria-label="Docente curricolare" className="flex-1">
                      Curricolare
                    </ToggleGroupItem>
                  </ToggleGroup>

                  <div className="mt-2 text-xs text-muted-foreground">
                    Influenza la modalità predefinita: <strong>Alunno</strong> per sostegno, <strong>Classe</strong> per curricolare.
                  </div>

                  {errors.teachingRole?.message ? (
                    <p className="mt-2 text-xs text-destructive">{String(errors.teachingRole.message)}</p>
                  ) : null}

                  <div className="mt-3 flex items-start gap-3">
                    <Checkbox
                      id="classCoordinator"
                      checked={Boolean(isClassCoordinator)}
                      onCheckedChange={(v) => setValue('isClassCoordinator', Boolean(v), { shouldDirty: true })}
                    />
                    <Label htmlFor="classCoordinator" className="text-sm text-muted-foreground leading-relaxed">
                      Sono coordinatore di classe
                    </Label>
                  </div>
                </div>
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="mario.rossi@scuola.edu.it"
                    className="pl-10"
                    {...register('email')}
                    aria-invalid={Boolean(errors.email)}
                  />
                </div>
                {errors.email?.message ? (
                  <p className="text-xs text-destructive">{String(errors.email.message)}</p>
                ) : null}
              </div>

              {/* Password */}
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    className="pl-10 pr-10"
                    {...register('password')}
                    aria-invalid={Boolean(errors.password)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                {passwordValue && (
                  <div className="space-y-1.5">
                    <div className="flex gap-1">
                      {[1, 2, 3, 4].map(i => (
                        <div key={i} className={`h-1 flex-1 rounded-full transition-colors ${
                          i <= passwordStrength
                            ? passwordStrength <= 1 ? 'bg-red-500' : passwordStrength <= 2 ? 'bg-amber-500' : passwordStrength <= 3 ? 'bg-emerald-400' : 'bg-emerald-500'
                            : 'bg-muted'
                        }`} />
                      ))}
                    </div>
                    <p className={`text-xs ${
                      passwordStrength <= 1 ? 'text-red-500' : passwordStrength <= 2 ? 'text-amber-500' : 'text-emerald-500'
                    }`}>
                      {passwordStrength <= 1 ? 'Debole' : passwordStrength <= 2 ? 'Discreta' : passwordStrength <= 3 ? 'Buona' : 'Forte'}
                      {' — '}{!passwordValue || passwordValue.length < 8 ? 'min 8 caratteri' : ''}{!/[A-Z]/.test(passwordValue) ? ' maiuscola' : ''}{!/[0-9]/.test(passwordValue) ? ' numero' : ''}
                    </p>
                  </div>
                )}
                {!passwordValue && <p className="text-xs text-muted-foreground">Min. 8 caratteri, 1 maiuscola, 1 numero</p>}
                {errors.password?.message ? (
                  <p className="text-xs text-destructive">{String(errors.password.message)}</p>
                ) : null}
              </div>

              {/* Confirm Password */}
              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Conferma Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    id="confirmPassword"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="••••••••"
                    className="pl-10"
                    {...register('confirmPassword')}
                    aria-invalid={Boolean(errors.confirmPassword)}
                  />
                </div>
                {errors.confirmPassword?.message ? (
                  <p className="text-xs text-destructive">{String(errors.confirmPassword.message)}</p>
                ) : null}
              </div>

              {/* Terms */}
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <Checkbox
                    id="terms"
                    checked={termsAccepted}
                    onCheckedChange={(v) => setTermsAccepted(Boolean(v))}
                  />
                  <Label htmlFor="terms" className="text-sm text-muted-foreground leading-relaxed">
                    Accetto i{' '}
                    <Link to={appPath('/terms-and-conditions')} className="text-brand hover:underline">
                      Termini e Condizioni
                    </Link>{' '}
                    e la{' '}
                    <Link to={appPath('/privacy-policy')} className="text-brand hover:underline">
                      Privacy Policy
                    </Link>.
                  </Label>
                </div>

                <div className="flex items-start gap-3">
                  <Checkbox
                    id="newsletter"
                    checked={newsletterOptIn}
                    onCheckedChange={(v) => setNewsletterOptIn(Boolean(v))}
                  />
                  <Label htmlFor="newsletter" className="text-sm text-muted-foreground leading-relaxed">
                    Voglio iscrivermi alla newsletter per ricevere aggiornamenti e novità.
                  </Label>
                </div>
              </div>

              {/* Submit */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full h-12 ds-btn-gradient"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    Registrazione in corso...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    Crea Account
                    <ChevronRight className="w-5 h-5" />
                  </span>
                )}
              </Button>
            </form>

            {/* Divider */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-card text-muted-foreground">oppure</span>
              </div>
            </div>

            {/* Login Link */}
            <p className="text-center text-muted-foreground">
              Hai già un account?{' '}
              <Link to={appPath('/login')} className="text-brand hover:underline font-medium">
                Accedi
              </Link>
            </p>
          </div>

          {/* Features reminder */}
          <div className="mt-6 text-center text-sm text-muted-foreground">
            <div className="flex flex-wrap justify-center gap-4">
              <span className="flex items-center gap-1">
                <Sparkles className="w-4 h-4 text-brand" />
                AI Didattica inclusa
              </span>
              <span className="flex items-center gap-1">
                <CheckCircle className="w-4 h-4 text-green-500" />
                7 giorni di prova gratuita
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
